import React, { useState } from 'react';

const BusinessExpensesForm = ({ data, updateData, nextStep, prevStep, businessCategory }) => {
    // Helpers for conditional rendering (if needed locally, or just rely on businessCategory prop)
    const isManufacturing = businessCategory === 'manufacturing';

    const [formData, setFormData] = useState({
        // 6. Financial Costs
        interestBusinessLoans: data?.interestBusinessLoans || '',
        creditCardFees: data?.creditCardFees || '',
        loanProcessingCharges: data?.loanProcessingCharges || '',

        // 7. Depreciation
        plantMachinery: data?.plantMachinery || '',
        furnitureFixtures: data?.furnitureFixtures || '',
        computersEquipment: data?.computersEquipment || '',
        vehicles: data?.vehicles || '',
        intangibleAssets: data?.intangibleAssets || '',

        // 8. Taxes & Duties
        gstPayments: data?.gstPayments || '',
        professionalTax: data?.professionalTax || '',
        tdsPayable: data?.tdsPayable || '',
        advanceTaxInstallments: data?.advanceTaxInstallments || '',
        gstExpenses: data?.gstExpenses || '', // Distinguish from payments if needed per image (GST Expenses)

        // 9. Miscellaneous (Existing)
        travelConveyance: data?.travelConveyance || '',
        hotelBoarding: data?.hotelBoarding || '',
        entertainment: data?.entertainment || '',
        repairMaintenance: data?.repairMaintenance || '',
        insuranceBusinessAssets: data?.insuranceBusinessAssets || '',
        securityServices: data?.securityServices || '',

        // Manufacturing Depreciation Specifics
        factoryBuilding: data?.factoryBuilding || '',

        // 9. Miscellaneous
        travelConveyance: data?.travelConveyance || '',
        hotelBoarding: data?.hotelBoarding || '',
        entertainment: data?.entertainment || '',
        repairMaintenance: data?.repairMaintenance || '',
        insuranceBusinessAssets: data?.insuranceBusinessAssets || '',
        securityServices: data?.securityServices || '',

        // Financial Strategy (New)
        businessReserveFund: data?.businessReserveFund || '',
        ownerDrawings: data?.ownerDrawings || '',
        termInsuranceOwner: data?.termInsuranceOwner || '',
        healthInsuranceOwner: data?.healthInsuranceOwner || '',
        healthInsuranceCover: data?.healthInsuranceCover || '',
        loanEMIMachinery: data?.loanEMIMachinery || '',
    });

    const [expandedSections, setExpandedSections] = useState({
        financial: false,
        depreciation: false,
        taxes: false,
        strategy: true, // Strategy section
        misc: false,
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateData(formData);
        nextStep();
    };

    return (
        <div className="form-step">
            <h2 className="step-title">Business Expenses & Investments</h2>
            <p className="step-description">Continue with your business expense details</p>

            <form onSubmit={handleSubmit} className="diagnosis-form">

                {isManufacturing ? (
                    /* ---------------- MANUFACTURING LAYOUT (F & G) ---------------- */
                    <>
                        {/* F. Financial & Statutory Costs */}
                        <div className="expense-section">
                            <h4 className="subsection-title collapsible" onClick={() => toggleSection('financial')}>
                                F. Financial & Statutory Costs
                                <span className="toggle-icon">{expandedSections.financial ? '▼' : '▶'}</span>
                            </h4>
                            {expandedSections.financial && (
                                <div className="grid-2-col">
                                    <InputGroup label="Interest on Business Loans" name="interestBusinessLoans" value={formData.interestBusinessLoans} onChange={handleChange} />
                                    <InputGroup label="Bank Charges" name="bankCharges" value={formData.bankCharges || ''} onChange={handleChange} />
                                    <InputGroup label="GST Expenses" name="gstExpenses" value={formData.gstExpenses} onChange={handleChange} />
                                    <InputGroup label="Professional Tax" name="professionalTax" value={formData.professionalTax} onChange={handleChange} />
                                    <InputGroup label="Advance Tax" name="advanceTaxInstallments" value={formData.advanceTaxInstallments} onChange={handleChange} />
                                </div>
                            )}
                        </div>

                        {/* G. Depreciation */}
                        <div className="expense-section">
                            <h4 className="subsection-title collapsible" onClick={() => toggleSection('depreciation')}>
                                G. Depreciation
                                <span className="toggle-icon">{expandedSections.depreciation ? '▼' : '▶'}</span>
                            </h4>
                            {expandedSections.depreciation && (
                                <div className="grid-2-col">
                                    <InputGroup label="Plant & Machinery" name="plantMachinery" value={formData.plantMachinery} onChange={handleChange} />
                                    <InputGroup label="Factory Building" name="factoryBuilding" value={formData.factoryBuilding} onChange={handleChange} />
                                    <InputGroup label="Furniture & Fixtures" name="furnitureFixtures" value={formData.furnitureFixtures} onChange={handleChange} />
                                    <InputGroup label="Computers & Equipment" name="computersEquipment" value={formData.computersEquipment} onChange={handleChange} />
                                    <InputGroup label="Vehicles" name="vehicles" value={formData.vehicles} onChange={handleChange} />
                                </div>
                            )}
                        </div>

                        {/* H. Financial Strategy & Reserves */}
                        <div className="expense-section">
                            <h4 className="subsection-title collapsible" onClick={() => toggleSection('strategy')}>
                                H. Financial Strategy & Reserves
                                <span className="toggle-icon">{expandedSections.strategy ? '▼' : '▶'}</span>
                            </h4>
                            {expandedSections.strategy && (
                                <div className="grid-2-col">
                                    <InputGroup label="Loan EMI (Plant & Machinery)" name="loanEMIMachinery" value={formData.loanEMIMachinery} onChange={handleChange} />
                                    <InputGroup label="Monthly Owner Drawings" name="ownerDrawings" value={formData.ownerDrawings} onChange={handleChange} />
                                    <InputGroup label="Business Reserve Fund" name="businessReserveFund" value={formData.businessReserveFund} onChange={handleChange} />
                                    <InputGroup label="Term Insurance Monthly Premium (Owner)" name="termInsuranceOwner" value={formData.termInsuranceOwner} onChange={handleChange} />
                                    <InputGroup label="Health Insurance Monthly Premium (Owner)" name="healthInsuranceOwner" value={formData.healthInsuranceOwner} onChange={handleChange} />
                                    <InputGroup label="Health Insurance Cover Amount (Owner)" name="healthInsuranceCover" value={formData.healthInsuranceCover} onChange={handleChange} />
                                    <InputGroup label="Insurance (Plant & Stock Portfolio)" name="insuranceBusinessAssets" value={formData.insuranceBusinessAssets} onChange={handleChange} />
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    /* ---------------- STANDARD LAYOUT ---------------- */
                    <>
                        {/* 6. Financial Costs */}
                        <div className="expense-section">
                            <h4 className="subsection-title collapsible" onClick={() => toggleSection('financial')}>
                                6. Financial Costs
                                <span className="toggle-icon">{expandedSections.financial ? '▼' : '▶'}</span>
                            </h4>
                            {expandedSections.financial && (
                                <>
                                    <div className="grid-2-col">
                                        <InputGroup label="Interest on Business Loans" name="interestBusinessLoans" value={formData.interestBusinessLoans} onChange={handleChange} />
                                        <InputGroup label="Credit Card Processing Fees" name="creditCardFees" value={formData.creditCardFees} onChange={handleChange} />
                                    </div>
                                    <InputGroup label="Loan Processing Charges" name="loanProcessingCharges" value={formData.loanProcessingCharges} onChange={handleChange} />
                                </>
                            )}
                        </div>

                        {/* 7. Depreciation */}
                        <div className="expense-section">
                            <h4 className="subsection-title collapsible" onClick={() => toggleSection('depreciation')}>
                                7. Depreciation & Amortization
                                <span className="toggle-icon">{expandedSections.depreciation ? '▼' : '▶'}</span>
                            </h4>
                            {expandedSections.depreciation && (
                                <>
                                    <div className="grid-2-col">
                                        <InputGroup label="Plant & Machinery" name="plantMachinery" value={formData.plantMachinery} onChange={handleChange} />
                                        <InputGroup label="Furniture & Fixtures" name="furnitureFixtures" value={formData.furnitureFixtures} onChange={handleChange} />
                                        <InputGroup label="Computers & Equipment" name="computersEquipment" value={formData.computersEquipment} onChange={handleChange} />
                                        <InputGroup label="Vehicles" name="vehicles" value={formData.vehicles} onChange={handleChange} />
                                    </div>
                                    <InputGroup label="Intangible Assets" name="intangibleAssets" value={formData.intangibleAssets} onChange={handleChange} />
                                </>
                            )}
                        </div>

                        {/* 8. Taxes & Duties */}
                        <div className="expense-section">
                            <h4 className="subsection-title collapsible" onClick={() => toggleSection('taxes')}>
                                8. Taxes & Duties
                                <span className="toggle-icon">{expandedSections.taxes ? '▼' : '▶'}</span>
                            </h4>
                            {expandedSections.taxes && (
                                <div className="grid-2-col">
                                    <InputGroup label="GST Payments (if applicable)" name="gstPayments" value={formData.gstPayments} onChange={handleChange} />
                                    <InputGroup label="Professional Tax" name="professionalTax" value={formData.professionalTax} onChange={handleChange} />
                                    <InputGroup label="TDS Payable" name="tdsPayable" value={formData.tdsPayable} onChange={handleChange} />
                                    <InputGroup label="Advance Tax Installments" name="advanceTaxInstallments" value={formData.advanceTaxInstallments} onChange={handleChange} />
                                </div>
                            )}
                        </div>

                        {/* 9. Miscellaneous */}
                        <div className="expense-section">
                            <h4 className="subsection-title collapsible" onClick={() => toggleSection('misc')}>
                                9. Miscellaneous
                                <span className="toggle-icon">{expandedSections.misc ? '▼' : '▶'}</span>
                            </h4>
                            {expandedSections.misc && (
                                <div className="grid-2-col">
                                    <InputGroup label="Travel & Conveyance (Business)" name="travelConveyance" value={formData.travelConveyance} onChange={handleChange} />
                                    <InputGroup label="Hotel & Boarding" name="hotelBoarding" value={formData.hotelBoarding} onChange={handleChange} />
                                    <InputGroup label="Entertainment (Business)" name="entertainment" value={formData.entertainment} onChange={handleChange} />
                                    <InputGroup label="Repair & Maintenance" name="repairMaintenance" value={formData.repairMaintenance} onChange={handleChange} />
                                    <InputGroup label="Insurance (Business Assets)" name="insuranceBusinessAssets" value={formData.insuranceBusinessAssets} onChange={handleChange} />
                                    <InputGroup label="Health Insurance Monthly Premium (Owner)" name="healthInsuranceOwner" value={formData.healthInsuranceOwner} onChange={handleChange} />
                                    <InputGroup label="Health Insurance Cover Amount (Owner)" name="healthInsuranceCover" value={formData.healthInsuranceCover} onChange={handleChange} />
                                    <InputGroup label="Security Services" name="securityServices" value={formData.securityServices} onChange={handleChange} />
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                    <button type="button" onClick={() => { updateData(formData); prevStep(); }} className="btn-secondary">← Back</button>
                    <button type="submit" className="btn-primary">Next: Investment & Income →</button>
                </div>
            </form>

            <style jsx>{`
                .section-heading {
                    color: #1f2937;
                    margin-bottom: 15px;
                    margin-top: 20px;
                    border-bottom: 2px solid #f3f4f6;
                    padding-bottom: 8px;
                    font-size: 1.1rem;
                }
                
                .expense-section {
                    background: #f9fafb;
                    padding: 15px;
                    border-radius: 8px;
                    margin-bottom: 15px;
                }
                
                .subsection-title {
                    color: #4b5563;
                    margin-bottom: 12px;
                    font-size: 0.95rem;
                    font-weight: 600;
                }
                
                .subsection-title.collapsible {
                    cursor: pointer;
                    user-select: none;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px;
                    background: #fff;
                    border-radius: 6px;
                    transition: background-color 0.2s;
                }
                
                .subsection-title.collapsible:hover {
                    background: #f3f4f6;
                }
                
                .toggle-icon {
                    font-size: 0.8rem;
                    color: #6b7280;
                }
                
                .grid-2-col {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 15px;
                }
                
                @media (max-width: 768px) {
                    .grid-2-col {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

const InputGroup = ({ label, name, value, onChange }) => {
    const inputValue = value === null || value === undefined ? '' : String(value);

    return (
        <div className="form-group" style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block', color: '#374151' }}>
                {label}
            </label>
            <div className="input-with-icon" style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>₹</span>
                <input
                    type="number"
                    name={name}
                    value={inputValue}
                    onChange={onChange}
                    placeholder="0"
                    style={{
                        paddingLeft: '25px',
                        width: '100%',
                        padding: '8px 10px 8px 25px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db'
                    }}
                    onWheel={(e) => {
                        e.target.blur();
                        e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                            e.preventDefault();
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default BusinessExpensesForm;
